import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onIdTokenChanged, signOut as fbSignOut, type User } from "firebase/auth";
import { auth } from "./firebase";

/** Eski Balder ile birebir 3 rol. */
export type UserRole = "boss" | "office" | "production";

export interface Session {
  user: User;
  role: UserRole;
  active: boolean;
  /** Bileşen ALIŞ fiyatı, satın alma, finans parametreleri. boss + office */
  costView: boolean;
  /** Ürün MALİYETİ, brüt kâr, kâr oranı, kârlılık raporu. YALNIZCA boss */
  financeView: boolean;
  displayName: string;
}

interface AuthState {
  session: Session | null;
  loading: boolean;
  /** Oturum açık ama rol atanmamış — ilk kurulum ekranı gösterilir. */
  needsSetup: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState>({
  session: null,
  loading: true,
  needsSetup: false,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // onIdTokenChanged (onAuthStateChanged DEĞİL): rol değişince sunucu token'ı
    // iptal eder, yenilenen token buradan anında yakalanır ve menü/yetki güncellenir.
    return onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setSession(null);
        setNeedsSetup(false);
        setLoading(false);
        return;
      }
      const { claims } = await user.getIdTokenResult();
      const role = claims.role as UserRole | undefined;
      const active = claims.active === true;

      // ── Rolü OLMAYAN hesap: kurulum bekliyor olabilir ────────────────────
      // İlk kurulumda henüz kimsenin rolü yoktur; burada oturumu kapatmak
      // `bootstrapFirstBoss` çağrısını imkânsız kılardı (yumurta-tavuk).
      // Rolsüz kullanıcı hiçbir veriye erişemez (Rules `active` claim'i arar),
      // yalnızca kurulum ekranını görür.
      if (!role) {
        setSession(null);
        setNeedsSetup(true);
        setLoading(false);
        return;
      }

      // Devre dışı bırakılmış hesap oturum AÇAMAZ. Gerçek koruma sunucuda
      // (Auth disabled + revokeRefreshTokens + Rules); bu yalnızca arayüz katmanı.
      if (!active) {
        await fbSignOut(auth);
        setSession(null);
        setNeedsSetup(false);
        setLoading(false);
        return;
      }

      setNeedsSetup(false);

      setSession({
        user,
        role,
        active,
        costView: claims.costView === true,
        financeView: claims.financeView === true,
        displayName: (claims.fullName as string) ?? user.email ?? "",
      });
      setLoading(false);
    });
  }, []);

  return (
    <Ctx.Provider value={{ session, loading, needsSetup, signOut: () => fbSignOut(auth) }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSession(): Session | null {
  return useContext(Ctx).session;
}

export function useAuth(): AuthState {
  return useContext(Ctx);
}

/** Menü ve buton görünürlüğü için. Gerçek koruma HER ZAMAN sunucudadır. */
export function useCan() {
  const s = useSession();
  return {
    isBoss: s?.role === "boss",
    isStaff: s?.role === "boss" || s?.role === "office",
    isProduction: s?.role === "production",
    /** Ürün maliyeti / kâr / kârlılık raporu */
    seeFinance: s?.financeView === true,
    /** Bileşen alış fiyatı / satın alma ekranı */
    seeCost: s?.costView === true,
    role: s?.role,
  };
}
